import 'mapbox-gl/dist/mapbox-gl.css';

import {
  EIconButtonVariant,
  GeneralMaximize2Icon,
  GeneralMinimize2Icon,
  IconButton,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import noop from 'lodash/noop';
import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  Map,
  MapLayerMouseEvent,
  MapRef,
  MapWheelEvent,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl';

import { ZOOM } from '@/constants/map';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useFooterVisibility } from '@/hooks/useFooterVisibility';
import { useIsFirstRender } from '@/hooks/useIsFirstRender';
import { trackBaseMapUpdatedEvent } from '@/services/analytics/maps';
import { OptimizelyFlags, useExperimentIsEnabled } from '@/services/experiments';
import { MAPBOX_STYLE_URLS, MAPBOX_STYLES, MapboxStyle } from '@/services/types/map';

import { MapLayers } from './MapLayers/MapLayers';
import ToggleSearch from './ToggleSearch';
import ZoomControls from './ZoomControls';

const TOKEN = process.env.mapboxApiToken;

type Props = {
  hasNavigation?: boolean;
  height?: number;
  loading: boolean;
  searchEnabled: boolean;
  width?: number;
  toggleSearch: React.ChangeEventHandler;
  onViewportChange?: (viewport: ViewStateChangeEvent) => void;
  onMapLoad?: () => void;
  onSearchMapButtonClick: () => void;
  onClick?: (e: MapLayerMouseEvent) => void;
  onWheel?: (e: MapWheelEvent) => void;
  children?: React.ReactNode;
  isDesktopMapViewExpanded: boolean;
  toggleExpandMapView: () => void;
  isOnPromoPage?: boolean;
  isOnListingPage?: boolean;
  allowPromoPageViewportChange?: boolean;
  viewport: Omit<ViewState, 'bearing' | 'pitch' | 'padding'>;
  hasMapLayers?: boolean;
};

const MapDynamic = React.forwardRef<MapRef, Props>(
  (
    {
      hasNavigation,
      viewport,
      loading,
      searchEnabled,
      toggleSearch,
      onViewportChange = noop,
      onMapLoad = noop,
      onSearchMapButtonClick,
      onClick,
      onWheel,
      children,
      isDesktopMapViewExpanded,
      toggleExpandMapView,
      isOnPromoPage = false,
      isOnListingPage = false,
      allowPromoPageViewportChange = false,
      hasMapLayers = true,
    },
    ref,
  ) => {
    const mapRef = useRef<MapRef>(null);
    const isFirstRender = useIsFirstRender();
    const isControlled = typeof viewport !== 'undefined';
    const currentViewportChangeHandler = isControlled ? onViewportChange : undefined;
    const isFooterVisible = useFooterVisibility(16, [isDesktopMapViewExpanded]);
    const { isAboveDesktop, isAboveXL } = useBreakpoint();

    const mapLayersExperimentEnabled = useExperimentIsEnabled(OptimizelyFlags.MAP_LAYERS);
    const [mapboxStyle, setMapboxStyle] = useState<MapboxStyle>(MAPBOX_STYLES.default);

    const expandButtonRef = useRef<HTMLDivElement>(null);
    const newNavigationControlRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => mapRef.current!);

    useEffect(() => {
      let expandButtonPosition = 0;
      const checkButtonIntersection = () => {
        if (expandButtonRef.current && newNavigationControlRef.current) {
          const rect1 = expandButtonRef.current.getBoundingClientRect();
          const rect2 = newNavigationControlRef.current.getBoundingClientRect();

          if (rect1.bottom > expandButtonPosition) {
            expandButtonPosition = rect1.bottom;
          }
          const intersect = rect1.bottom >= rect2.top;
          newNavigationControlRef.current.style.visibility = intersect ? 'hidden' : 'visible';
        }
      };

      window.addEventListener('scroll', checkButtonIntersection);
      checkButtonIntersection(); // Call it immediately to set the initial state

      return () => {
        window.removeEventListener('scroll', checkButtonIntersection);
      };
    }, []);

    useEffect(() => {
      if (isOnPromoPage) return;
      if (expandButtonRef.current && isAboveXL) {
        const el = expandButtonRef.current.getBoundingClientRect();
        expandButtonRef.current.style.top = `${el.top - 58}px`;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAboveXL]);

    const handleViewportChange = useCallback(
      (viewport: ViewStateChangeEvent) => {
        if (isFirstRender) {
          return;
        }
        currentViewportChangeHandler?.(viewport);
      },
      [isFirstRender, currentViewportChangeHandler],
    );

    const handleMapStyleChange = useCallback(
      (newMapStyle: MapboxStyle) => {
        if (newMapStyle === mapboxStyle) {
          // skip repeated clicks on the same style
          return;
        }

        const previousMapStyle = mapboxStyle;
        setMapboxStyle(newMapStyle);

        trackBaseMapUpdatedEvent({ oldBase: previousMapStyle, newBase: newMapStyle });
      },
      [mapboxStyle],
    );

    const handleZoom = useCallback((relativeZoom: number) => {
      if (!mapRef.current) return;

      if (relativeZoom > 0) {
        mapRef.current.zoomIn();
      } else {
        mapRef.current.zoomOut();
      }
    }, []);

    useEffect(() => {
      mapRef.current?.resize();
    }, [isDesktopMapViewExpanded]);

    return (
      <div data-testid="map-dynamic" className="relative w-full h-full">
        <Map
          ref={mapRef}
          onWheel={onWheel}
          mapStyle={MAPBOX_STYLE_URLS[mapboxStyle]}
          mapboxAccessToken={TOKEN}
          onMove={handleViewportChange}
          onLoad={onMapLoad}
          minZoom={ZOOM.MIN}
          maxZoom={ZOOM.MAX}
          onClick={onClick}
          dragRotate={false}
          {...viewport}>
          {children}
        </Map>
        {!isAboveXL ? (
          <div className="absolute top-0 left-0 flex items-center p-3 z-15">
            {mapLayersExperimentEnabled && hasMapLayers && (
              <MapLayers
                activeStyle={mapboxStyle}
                isDesktopMapViewExpanded={isDesktopMapViewExpanded}
                setMapboxStyle={handleMapStyleChange}
              />
            )}
            {(!isOnPromoPage || (allowPromoPageViewportChange && !isAboveDesktop)) && (
              <ToggleSearch
                checked={searchEnabled}
                loading={loading}
                onChange={toggleSearch}
                onClick={onSearchMapButtonClick}
              />
            )}

            {hasNavigation && <ZoomControls zoom={viewport.zoom} onZoom={handleZoom} />}
          </div>
        ) : (
          <>
            <div className="absolute top-0 left-0 h-full pointer-events-none">
              <div
                className="sticky left-0 z-20 flex items-center m-5 pointer-events-auto"
                ref={expandButtonRef}>
                {mapLayersExperimentEnabled && hasMapLayers && (
                  <MapLayers
                    isDesktopMapViewExpanded={isDesktopMapViewExpanded}
                    activeStyle={mapboxStyle}
                    setMapboxStyle={handleMapStyleChange}
                  />
                )}
                <IconButton
                  className="!text-750"
                  icon={isDesktopMapViewExpanded ? GeneralMinimize2Icon : GeneralMaximize2Icon}
                  onClick={toggleExpandMapView}
                  size="small"
                  variant={EIconButtonVariant.Primary}
                />
              </div>
            </div>
            <div
              className={clsx('flex items-center left-0 m-5 z-10 ', {
                'sticky bottom-5 -mt-20': isOnListingPage,
                'sticky bottom-5':
                  (isFooterVisible && isOnPromoPage && !isOnListingPage) ||
                  (!isFooterVisible && !isOnPromoPage && !isOnListingPage),
                'absolute mb-10 bottom-0':
                  (!isFooterVisible && isOnPromoPage && !isOnListingPage) ||
                  (isFooterVisible && !isOnPromoPage && !isOnListingPage),
              })}
              ref={newNavigationControlRef}>
              {hasNavigation && (
                <ZoomControls
                  zoom={viewport.zoom}
                  onZoom={handleZoom}
                  isDesktopMapViewEnabled={isDesktopMapViewExpanded}
                />
              )}
            </div>
          </>
        )}
      </div>
    );
  },
);

MapDynamic.displayName = 'MapDynamic';

export default MapDynamic;
